import _get from 'lodash/get';
import _set from 'lodash/set';
import type { Paths } from 'type-fest';

import type {
  ApplicationFormData,
  EducationDetails,
  InsuranceAgentWorkDetails,
  LegalEntity,
  OldFioDetails,
  RelativeDetails,
  SelfEmployedWorkDetails,
  WorkDetails,
} from '../../../api';
import {
  type FormsStepsValues,
  Steps,
  type StepsValues,
} from '../../constants';
import type { State } from '../../types';

type ApplicationFormDataKeys = Paths<ApplicationFormData>;
type FlagsKeys = Extract<ApplicationFormDataKeys, `${string}isNo${string}`>;
type DetailsKeys = Extract<
  ApplicationFormDataKeys,
  `${string}Details` | `${string}Entities`
>;

const mapSteps: Record<
  FormsStepsValues,
  {
    flag: FlagsKeys;
    details: DetailsKeys;
    nextStep: StepsValues;
  }
> = {
  [Steps.PREVIOUS_NAMES_FORM]: {
    flag: 'oldFioData.isNoChangeFio',
    details: 'oldFioData.oldFioDetails',
    nextStep: Steps.CIVIL_SERVICE_EXPERIENCE_QUESTION,
  },
  [Steps.RGS_EMPLOYEE_RELATIVES_FORM]: {
    flag: 'relativesData.isNoRelatives',
    details: 'relativesData.relativesDetails',
    nextStep: Steps.PROFESSIONAL_EDUCATION_QUESTION,
  },
  [Steps.PROFESSIONAL_EDUCATION_FORM]: {
    flag: 'educationData.isNoEducation',
    details: 'educationData.educationDetails',
    nextStep: Steps.INSURANCE_AGENT_EXPERIENCE_QUESTION,
  },
  [Steps.INSURANCE_AGENT_EXPERIENCE_LIST_FORM]: {
    flag: 'insuranceAgentWorkData.isNoInsuranceAgentWork',
    details: 'insuranceAgentWorkData.insuranceAgentWorkDetails',
    nextStep: Steps.INSURANCE_AGENT_EXPERIENCE_DETAILS_FORM,
  },
  [Steps.INSURANCE_AGENT_EXPERIENCE_DETAILS_FORM]: {
    flag: 'insuranceAgentWorkData.isNoInsuranceAgentWork',
    details: 'insuranceAgentWorkData.insuranceAgentWorkDetails',
    nextStep: Steps.OTHER_WORK_EXPERIENCE_QUESTION,
  },
  [Steps.OTHER_WORK_EXPERIENCE_FORM]: {
    flag: 'workData.isNoWork',
    details: 'workData.workDetails',
    nextStep: Steps.LEGAL_ENTITY_FOUNDER_STATUS_QUESTION,
  },
  [Steps.LEGAL_ENTITY_FOUNDER_STATUS_FORM]: {
    flag: 'legalEntitiesData.isNoLegalEntities',
    details: 'legalEntitiesData.legalEntities',
    nextStep: Steps.SELF_EMPLOYMENT_STATUS_QUESTION,
  },
  [Steps.SELF_EMPLOYMENT_STATUS_FORM]: {
    flag: 'selfEmployedWorkData.isNoSelfEmployedWork',
    details: 'selfEmployedWorkData.selfEmployedWorkDetails',
    nextStep: Steps.QUESTIONNAIRE_RESULTS,
  },
};

export interface DetailsMap {
  [Steps.PREVIOUS_NAMES_FORM]: OldFioDetails[];
  [Steps.RGS_EMPLOYEE_RELATIVES_FORM]: RelativeDetails[];
  [Steps.PROFESSIONAL_EDUCATION_FORM]: EducationDetails[];
  [Steps.INSURANCE_AGENT_EXPERIENCE_LIST_FORM]: InsuranceAgentWorkDetails[];
  [Steps.INSURANCE_AGENT_EXPERIENCE_DETAILS_FORM]: InsuranceAgentWorkDetails[];
  [Steps.OTHER_WORK_EXPERIENCE_FORM]: WorkDetails[];
  [Steps.LEGAL_ENTITY_FOUNDER_STATUS_FORM]: LegalEntity[];
  [Steps.SELF_EMPLOYMENT_STATUS_FORM]: SelfEmployedWorkDetails[];
}

export type FormDetailsArgs = {
  [S in keyof DetailsMap]: [formStep: S, details: DetailsMap[S]];
}[keyof DetailsMap];

export const getFormUpdate = (
  state: State,
  ...[formStep, details]: FormDetailsArgs
): {
  nextStep: StepsValues;
  formUpdate?: ApplicationFormData;
} => {
  const stepObj = mapSteps[formStep];

  const nextStep = stepObj.nextStep;
  let formUpdate: undefined | ApplicationFormData;
  const prevDetailsValue = _get(state, stepObj.details);
  const nextDetailsValue = details;

  if (nextDetailsValue !== prevDetailsValue) {
    formUpdate = {};

    _set(formUpdate, stepObj.flag, false);

    if (stepObj.details) {
      _set(formUpdate, stepObj.details, nextDetailsValue);
    }
  }

  return {
    nextStep,
    formUpdate,
  };
};
